/**
 * ptt.ts — PUSH-TO-TALK, the key's state (the gaps wave, round 2, 2026-09-28). Pure: no DOM, no module state.
 *
 * On macOS a Caps Lock HOLD cannot be seen by a web page: the OS toggles the lock on the press, and the browser fires
 * `keydown` when the lock turns ON and `keyup` only when it turns OFF — at the NEXT press. Held push-to-talk on the
 * default key (Caps Lock) therefore never worked on a Mac: the first press opened the mic and releasing it did
 * nothing. So on a Mac, Caps Lock push-to-talk is a LATCH whose state IS the lock — the keyboard's own light is the
 * talk light (chosen over a different default key: no free key is left — every letter means something, and Right Alt
 * is AltGr on international keyboards — and one key named the same on every platform keeps the Controls row, the pill
 * and the help honest):
 *   · the mic OPENS only on a Caps Lock event outside a text field that leaves the lock ON;
 *   · it CLOSES on a Caps Lock event that leaves the lock OFF, on ANY other key or pointer event that reports the lock
 *     OFF (it was turned off while typing in the chat), and on blur — after a blur it waits for a fresh Caps event (a
 *     lock left on is never a mic reopened behind the player's back);
 *   · typing capitals in a text field never opens it.
 * Everywhere else — and for any other key bound to push-to-talk, on a Mac too — it is a HOLD: keydown opens, keyup
 * closes, a key repeat is nothing, a keydown in a text field is ignored.
 */

export interface NavLike { platform?: string; userAgent?: string; userAgentData?: { platform?: string } | null }

/** macOS (and an iPad that reports itself as a Mac): the platforms whose Caps Lock cannot be held. */
export function isMacPlatform(nav: NavLike | null | undefined): boolean {
  if (!nav) return false;
  const uad = nav.userAgentData?.platform ?? '';
  if (uad) return /mac/i.test(uad);
  return /mac/i.test(nav.platform ?? '') || /Macintosh|Mac OS X/.test(nav.userAgent ?? '');
}

/** Caps Lock push-to-talk is a latch when the platform is a Mac and Caps Lock is one of the bound keys. */
export function pttLatchMode(isMac: boolean, codes: readonly string[]): boolean {
  return isMac && codes.includes('CapsLock');
}

export type PttEvent =
  | { kind: 'key'; down: boolean; code: string; isPtt: boolean; capsOn: boolean | null; inField: boolean; repeat: boolean }
  | { kind: 'other'; capsOn: boolean | null }
  | { kind: 'blur' };

/** The push-to-talk state after an event. `latch` = pttLatchMode(…) for the bound keys. */
export function nextPtt(held: boolean, ev: PttEvent, latch: boolean): boolean {
  if (ev.kind === 'blur') return false;
  if (latch) {
    const caps = ev.kind === 'key' && ev.isPtt && ev.code === 'CapsLock';
    if (caps) {
      if (ev.kind !== 'key') return held;
      if (ev.inField) return ev.capsOn === false ? false : held; // typing capitals never opens the mic
      if (ev.capsOn !== null) return ev.capsOn; // the lock's state IS the talk state
      return ev.down; // a browser that reports no modifier state: keydown on, keyup off
    }
    if (ev.kind === 'key' && ev.isPtt) {
      // Another key bound beside Caps Lock is still a hold.
      if (ev.repeat || (ev.down && ev.inField)) return held;
      return ev.down;
    }
    return ev.capsOn === false ? false : held; // the lock went off elsewhere (a chat box): the mic closes with it
  }
  if (ev.kind !== 'key' || !ev.isPtt) return held;
  if (ev.repeat) return held;
  if (ev.down && ev.inField) return held;
  return ev.down;
}

/** What the pill and the Settings row say about push-to-talk. */
export function pttHint(latch: boolean, held: boolean, keyText: string): string {
  if (latch) return held ? 'talking — Caps Lock is on' : 'tap Caps Lock to talk';
  return held ? 'talking' : `hold ${keyText}`;
}
